import * as THREE from "three";
import { createHud } from "../../hud";
import { createWiringTrainerInteraction } from "../../interaction/WiringTrainerInteraction";
import { Trainer } from "../../trainer/Trainer";
import type { InteractionSystem } from "../../xr/input";
import type { XRRig } from "../../xr/session";
import { createResetPlate } from "../../world/resetPlate";
import { buildTrainingStation } from "../../world/station";
import type { WorkshopStation } from "../WorkshopStation";

export const OUTLET_ASSEMBLY_STATION_ID = "outlet-assembly";

export interface OutletAssemblyStation extends WorkshopStation {
  readonly id: typeof OUTLET_ASSEMBLY_STATION_ID;
  /** Outlet trainer step machine (station-local). */
  readonly trainer: Trainer;
}

export interface CreateOutletAssemblyOptions {
  rig: XRRig;
  interaction: InteractionSystem;
  /**
   * Optional parent for the station root. Defaults to `rig.scene`.
   * Environment specialist may pass a workshop floor group later.
   */
  parent?: THREE.Object3D;
}

/**
 * Outlet Assembly — one free-exploration station in the Electrical Workshop.
 *
 * Wraps the existing HOT/NEUTRAL/GROUND snap + receptacle mount + Trainer steps.
 * Completion here does not unlock other stations; other benches register independently.
 *
 * Reset: plate on this bench and Workshop KeyR (nearest/active) call {@link reset}.
 * Does not reset the player pose or other stations.
 */
export function createOutletAssemblyStation(
  opts: CreateOutletAssemblyOptions,
): OutletAssemblyStation {
  const { rig, interaction } = opts;
  const parent = opts.parent ?? rig.scene;

  const sceneRoots = buildTrainingStation(rig.scene);
  // buildTrainingStation already adds root to scene; re-parent if needed.
  if (sceneRoots.root.parent !== parent) {
    parent.add(sceneRoots.root);
  }

  const trainer = new Trainer();
  const hud = createHud();
  // HUD beside the wall-box, local to the rotated workbench.
  hud.mesh.position.set(0.48, 0.48, 0.2);
  hud.mesh.rotation.y = 0.12;
  sceneRoots.workbench.add(hud.mesh);
  hud.setStep(trainer.getStep());
  trainer.subscribe((step) => hud.setStep(step));

  const wiring = createWiringTrainerInteraction(
    rig,
    interaction,
    trainer,
    sceneRoots,
  );

  // Station-local reset only: do not call interaction.reset() here — that
  // synthesizes squeeze-end / clears ownership globally and would drop a live
  // V/I/R knob turn. Session boundaries in main.ts still clear sticky input.

  const station: OutletAssemblyStation = {
    id: OUTLET_ASSEMBLY_STATION_ID,
    root: sceneRoots.root,
    activationAnchor: sceneRoots.workbench,
    activationRadius: 2.8,
    active: false,
    trainer,

    showIntro() {
      // HUD already shows current step; reserved for future coaching overlay.
    },
    hideIntro() {
      // No-op until intro cards exist.
    },

    reset() {
      // Drop holds first so restoreAll does not fight a live squeeze.
      wiring.releaseSessionHolds();
      trainer.reset();
    },

    update(dt: number, _playerWorldPos: THREE.Vector3) {
      wiring.update(dt);
    },

    releaseSessionHolds() {
      wiring.releaseSessionHolds();
    },
  };

  // Reset plate is station-scoped (not a global workshop wipe).
  const resetPlate = createResetPlate(interaction, () => station.reset());
  resetPlate.position.set(0.82, 0.02, 0.18);
  sceneRoots.workbench.add(resetPlate);

  return station;
}
